import { Button, Container, NumericInput, Element } from '@playcanvas/pcui';
import { TranslateGizmo, Vec3 } from 'playcanvas';


import { BoxShape } from '../box-shape';
import { Events } from '../events';
import { Scene } from '../scene';
import { Splat } from '../splat';
import { localize } from '../ui/localization';

class BoxSelection {
    activate: () => void;
    deactivate: () => void;

    active = false;

    constructor(events: Events, scene: Scene, canvasContainer: Container) {
        const box = new BoxShape();

        const gizmo = new TranslateGizmo(scene.camera.entity.camera, scene.gizmoLayer);

        gizmo.on('render:update', () => {
            scene.forceRender = true;
        });

        gizmo.on('transform:move', () => {
            box.moved();
        });

        // ui
        const selectToolbar = new Container({
            id: 'select-toolbar',
            hidden: true
        });

        selectToolbar.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
        });

        const setButton = new Button({ text: 'Set', class: 'select-toolbar-button' });
        const addButton = new Button({ text: 'Add', class: 'select-toolbar-button' });
        const removeButton = new Button({ text: 'Remove', class: 'select-toolbar-button' });
        const selectionSeparator = new Element({ class: 'bottom-toolbar-separator' });
        const selectionSeparator2 = new Element({ class: 'bottom-toolbar-separator' });
        const invertButton = new Button({ text: localize('select.invert'), class: 'select-toolbar-button' });
        const unselectButton = new Button({ text: localize('select.none'), class: 'select-toolbar-button' });
        const deleteButton = new Button({ text: localize('select.delete'), id: 'select-toolbar-delete-button', class: 'select-toolbar-button' });

        const lenX = new NumericInput({
            precision: 2,
            value: box.lenX,
            placeholder: 'LenX',
            width: 80,
            min: 0.01
        });

        const lenY = new NumericInput({
            precision: 2,
            value: box.lenY,
            placeholder: 'LenY',
            width: 80,
            min: 0.01
        });

        const lenZ = new NumericInput({
            precision: 2,
            value: box.lenZ,
            placeholder: 'LenZ',
            width: 80,
            min: 0.01
        });

        selectToolbar.append(lenX);
        selectToolbar.append(lenY);
        selectToolbar.append(lenZ);
        selectToolbar.append(new Element({ class: 'bottom-toolbar-separator' }));
        selectToolbar.append(setButton);
        selectToolbar.append(addButton);
        selectToolbar.append(removeButton);
        selectToolbar.append(selectionSeparator);
        selectToolbar.append(invertButton);
        selectToolbar.append(unselectButton);
        selectToolbar.append(selectionSeparator2);
        selectToolbar.append(deleteButton);


        canvasContainer.append(selectToolbar);

        const apply = (op: 'set' | 'add' | 'remove') => {
            const p = box.pivot.getPosition();
            events.fire('select.byBox', op, [p.x, p.y, p.z, box.lenX, box.lenY, box.lenZ]);
        };

        const setButtonVisibility = () => {
            if (events.invoke('selection.splats')) {
                selectionSeparator.hidden = false;
                selectionSeparator2.hidden = false;
                unselectButton.hidden = false;
                deleteButton.hidden = false;
                invertButton.hidden = false;
            } else {
                selectionSeparator.hidden = true;
                selectionSeparator2.hidden = true;
                unselectButton.hidden = true;
                deleteButton.hidden = true;
                invertButton.hidden = true;
            }
        };

        setButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation(); apply('set'); setButtonVisibility();
        });
        addButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation(); apply('add'); setButtonVisibility();
        });
        removeButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation(); apply('remove'); setButtonVisibility();
        });
        unselectButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            events.fire('select.none'); setButtonVisibility();
        });
        deleteButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            events.fire('select.delete'); setButtonVisibility();
        });
        invertButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            events.fire('select.invert'); setButtonVisibility();
        });
        lenX.on('change', () => {
            box.lenX = lenX.value;
        });
        lenY.on('change', () => {
            box.lenY = lenY.value;
        });
        lenZ.on('change', () => {
            box.lenZ = lenZ.value;
        });

        events.on('camera.focalPointPicked', (details: { splat: Splat, position: Vec3 }) => {
            if (this.active) {
                box.pivot.setPosition(details.position);
                gizmo.attach([box.pivot]);
            }
        });

        const updateGizmoSize = () => {
            const { camera, canvas } = scene;
            if (camera.ortho) {
                gizmo.size = 1125 / canvas.clientHeight;
            } else {
                gizmo.size = 1200 / Math.max(canvas.clientWidth, canvas.clientHeight);
            }
        };
        updateGizmoSize();
        events.on('camera.resize', updateGizmoSize);
        events.on('camera.ortho', updateGizmoSize);


        this.activate = () => {
            this.active = true;
            scene.add(box);
            gizmo.attach([box.pivot]);
            selectToolbar.hidden = false;
            setButtonVisibility();

        };

        this.deactivate = () => {
            gizmo.detach();
            scene.remove(box);
            this.active = false;
            selectToolbar.hidden = true;
            setButtonVisibility();

        };
    }
}

export { BoxSelection };
